"use client"

import { useState, useEffect } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import {
  FaArrowLeft,
  FaBox,
  FaFilter,
  FaSearch,
  FaCar,
  FaMoneyBillWave,
  FaRulerHorizontal,
  FaUser,
  FaInfoCircle,
  FaEdit,
  FaTimes,
  FaSave,
} from "react-icons/fa"
import Sidebar from "../SidebarInAdminPanel/Sidebar"
import { useMemo } from "react"

// Import the Firebase controllers
import { 
  fetchRideRequestsByUserId, 
  fetchAllRideRequests, 
  updateRideRequestStatus, 
  deleteRideRequest 
} from "../../Controllers/rideController.js"
import { 
  fetchPostRequestsByDriverId, 
  fetchAllPostRequests, 
  updatePostRequestStatus, 
  deletePostRequest 
} from "../../Controllers/postsController.js"

const UserRequestsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  // Get URL parameters - support both path params and query params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const userIdFromQuery = searchParams.get("userId")
  const userIdFromPath = params.userId
  const userId = userIdFromPath || userIdFromQuery

  const userTypeFromUrl = searchParams.get("userType")

  const [userType] = useState(userTypeFromUrl || "User") // "User" or "Driver"
  const [activeTab, setActiveTab] = useState("all") // all, pending, completed, cancelled
  const [searchTerm, setSearchTerm] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [sortBy, setSortBy] = useState("date") // date, price, priority
  const [sortOrder, setSortOrder] = useState("desc") // asc, desc
  const [loading, setLoading] = useState(true)
  const [expandedRequestId, setExpandedRequestId] = useState(null)
  const [editingRequestId, setEditingRequestId] = useState(null)
  const [editFormData, setEditFormData] = useState({})
  const [requestsData, setRequestsData] = useState({
    rideRequests: [],
    postRequests: [],
  })
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [requestToCancel, setRequestToCancel] = useState(null)
  const [error, setError] = useState(null)

  // Log for debugging
  useEffect(() => {
    console.log("Component mounted with userId:", userId)
    console.log("UserType:", userType)
  }, [userId, userType])

  // Fetch requests data based on user type and userId
  useEffect(() => {
    const fetchRequestsData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        let rideRequests = []
        let postRequests = []

        if (userType === "User") {
          // Fetch ride requests made by users
          if (userId) {
            rideRequests = await fetchRideRequestsByUserId(userId)
          } else {
            rideRequests = await fetchAllRideRequests()
            // Filter to show only requests made by users (not drivers)
            rideRequests = rideRequests.filter(req => req.userProfile?.userType === "User")
          }
        } else if (userType === "Driver") {
          // Fetch post requests made by drivers
          if (userId) {
            postRequests = await fetchPostRequestsByDriverId(userId)
          } else {
            postRequests = await fetchAllPostRequests()
            // Filter to show only requests made by drivers
            postRequests = postRequests.filter(req => req.riderProfile?.userType === "Driver")
          }
        }

        setRequestsData({
          rideRequests: rideRequests || [],
          postRequests: postRequests || [],
        })
      } catch (err) {
        console.error("Error fetching requests:", err)
        setError("Failed to load requests. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchRequestsData()
  }, [userType, userId])

  // Get the appropriate requests based on user type
  const requests = userType === "User" ? requestsData.rideRequests : requestsData.postRequests

  // Helper function to get priority from request data
  const getPriority = (request) => {
    if (request.status === "pending") return "medium"
    if (request.status === "accepted" || request.status === "started") return "high"
    if (request.status === "completed") return "low"
    return "low"
  }

  // Helper function to format Firebase timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return new Date()
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000)
    }
    if (timestamp.toDate) {
      return timestamp.toDate()
    }
    return new Date(timestamp)
  }

  // Helper function to format address from city object
  const formatAddress = (cityObj, address) => {
    if (!cityObj) return address || "N/A"
    
    const parts = []
    if (address) parts.push(address)
    if (cityObj.address_city && cityObj.address_city !== address) parts.push(cityObj.address_city)
    if (cityObj.address_district) parts.push(cityObj.address_district)
    
    return parts.length > 0 ? parts.join(", ") : cityObj.formatted_address || "N/A"
  }

  // Helper function to format request for display
  const formatRequestForDisplay = (request) => {
    const createdAt = formatTimestamp(request.createdAt)
    
    if (userType === "User") {
      // For ride requests made by users - postData contains the post information
      const postData = request.postData || {}
      
      return {
        id: request.id,
        type: "Ride Request",
        status: request.status,
        priority: getPriority(request),
        category: "Transportation",
        createdAt: createdAt,
        description: `Request to deliver: ${postData.description || 'Package'}`,
        from: formatAddress(postData.pickupCity, postData.pickupAddress),
        to: formatAddress(postData.dropoffCity, postData.dropoffAddress),
        date: postData.departureDate ? new Date(postData.departureDate).toLocaleDateString() : "Nn/A",
        time: postData.departureTime ? new Date(postData.departureTime).toLocaleTimeString() : "Nn/A",
        cost: postData.estimatedCost ? `Rs. ${postData.estimatedCost}` : "N/A",
        size: postData.size || "N/A",
        weight: postData.weight || "N/A",
        distance: postData.estimatedDistance ? `${postData.estimatedDistance} km` : "N/A",
        userType: "User",
        requesterId: request.userId,
        requesterName: postData?.userName || "Unknown User",
        requesterImage: postData?.userProfileImage,
        driverName: request.riderProfile?.fullname || "Unknown Driver",
        driverImage: request.riderProfile?.profileImage,
        packageImage: postData.image,
        specialInstructions: postData.specialInstructions,
        verificationCode: postData.verificationCode,
        requestData: request,
      }
    } else {
      // For post requests made by drivers - rideData contains the ride information
      const rideData = request.rideData || {}
      const postDetails = request.postDetails || {}
      
      return {
        id: request.id,
        type: "Post Request",
        status: request.status,
        priority: getPriority(request),
        category: "Delivery",
        createdAt: createdAt,
        description: `Request to deliver: ${postDetails.description || 'Package'}`,
        from: rideData.departureLocation || "N/A",
        to: rideData.destinationLocation || "N/A",
        date: rideData.departureDate ? new Date(rideData.departureDate).toLocaleDateString() : "N/A",
        time: rideData.departureTime ? new Date(rideData.departureTime).toLocaleTimeString() : "N/A",
        cost: postDetails.estimatedCost ? `Rs. ${postDetails.estimatedCost}` : "N/A",
        size: postDetails.size || "N/A",
        weight: postDetails.weight || "N/A",
        distance: postDetails.estimatedDistance ? `${postDetails.estimatedDistance} km` : "N/A",
        vehicleType: rideData.transportType || "N/A",
        availableSpace: rideData.availableSpace || "N/A",
        userType: "Driver",
        requesterId: request.riderId,
        requesterName: rideData?.userName || "Unknown Driver",
        requesterImage: rideData?.userProfileImage,
        posterName: rideData?.userName || "Unknown User",
        posterImage: request.posterProfile?.profileImage,
        packageImage: postDetails.image,
        specialInstructions: postDetails.specialInstructions,
        verificationCode: postDetails.verificationCode,
        requestData: request,
      }
    }
  }

  // Handle cancel request
  const handleCancelRequest = (requestId) => {
    setRequestToCancel(requestId)
    setShowCancelModal(true)
  }

  const confirmCancelRequest = async () => {
    if (requestToCancel) {
      try {
        let result
        if (userType === "User") {
          result = await updateRideRequestStatus(requestToCancel, "cancelled")
        } else {
          result = await updatePostRequestStatus(requestToCancel, "cancelled")
        }

        if (result.success) {
          // Update local state
          setRequestsData((prev) => {
            const updatedData = { ...prev }
            if (userType === "User") {
              updatedData.rideRequests = prev.rideRequests.map((req) =>
                req.id === requestToCancel ? { ...req, status: "cancelled" } : req,
              )
            } else {
              updatedData.postRequests = prev.postRequests.map((req) =>
                req.id === requestToCancel ? { ...req, status: "cancelled" } : req,
              )
            }
            return updatedData
          })
          alert("Request cancelled successfully!")
        } else {
          throw new Error("Failed to cancel request")
        }
      } catch (error) {
        console.error("Error cancelling request:", error)
        alert("Failed to cancel request. Please try again.")
      }

      setShowCancelModal(false)
      setRequestToCancel(null)
    }
  }

  // Handle delete request
  const handleDeleteRequest = async (requestId) => {
    if (window.confirm("Are you sure you want to delete this request? This action cannot be undone.")) {
      try {
        let result
        if (userType === "User") {
          result = await deleteRideRequest(requestId)
        } else {
          result = await deletePostRequest(requestId)
        }

        if (result.success) {
          // Update local state
          setRequestsData((prev) => {
            const updatedData = { ...prev }
            if (userType === "User") {
              updatedData.rideRequests = prev.rideRequests.filter((req) => req.id !== requestId)
            } else {
              updatedData.postRequests = prev.postRequests.filter((req) => req.id !== requestId)
            }
            return updatedData
          })
          alert("Request deleted successfully!")
        } else {
          throw new Error("Failed to delete request")
        }
      } catch (error) {
        console.error("Error deleting request:", error)
        alert("Failed to delete request. Please try again.")
      }
    }
  }

  // Handle edit request
  const handleEditRequest = (request) => {
    setEditingRequestId(request.id)
    setEditFormData({
      status: request.status,
    })
  }

  const handleSaveEdit = async (requestId) => {
    try {
      let result
      if (userType === "User") {
        result = await updateRideRequestStatus(requestId, editFormData.status)
      } else {
        result = await updatePostRequestStatus(requestId, editFormData.status)
      }

      if (result.success) {
        // Update local state
        setRequestsData((prev) => {
          const updatedData = { ...prev }
          if (userType === "User") {
            updatedData.rideRequests = prev.rideRequests.map((req) =>
              req.id === requestId ? { ...req, status: editFormData.status } : req,
            )
          } else {
            updatedData.postRequests = prev.postRequests.map((req) =>
              req.id === requestId ? { ...req, status: editFormData.status } : req,
            )
          }
          return updatedData
        })
        alert("Request updated successfully!")
      } else {
        throw new Error("Failed to update request")
      }
    } catch (error) {
      console.error("Error updating request:", error)
      alert("Failed to update request. Please try again.")
    }

    setEditingRequestId(null)
    setEditFormData({})
  }

  const handleCancelEdit = () => {
    setEditingRequestId(null)
    setEditFormData({})
  }

  // Filter and sort requests
  const getFilteredRequests = () => {
    const formattedRequests = requests.map(formatRequestForDisplay)
    
    return formattedRequests
      .filter((request) => {
        // Filter by tab
        if (activeTab !== "all" && request.status !== activeTab) {
          return false
        }

        // Filter by search term
        if (
          searchTerm &&
          !Object.values(request).some(
            (value) => typeof value === "string" && value.toLowerCase().includes(searchTerm.toLowerCase()),
          )
        ) {
          return false
        }

        return true
      })
      .sort((a, b) => {
        // Sort by selected field
        if (sortBy === "date") {
          return sortOrder === "asc"
            ? new Date(a.createdAt) - new Date(b.createdAt)
            : new Date(b.createdAt) - new Date(a.createdAt)
        }

        if (sortBy === "price") {
          const aPrice = a.cost || "0"
          const bPrice = b.cost || "0"
          const aPriceNum = typeof aPrice === 'string' ? Number.parseInt(aPrice.replace(/[^\d]/g, '')) || 0 : aPrice
          const bPriceNum = typeof bPrice === 'string' ? Number.parseInt(bPrice.replace(/[^\d]/g, '')) || 0 : bPrice
          return sortOrder === "asc" ? aPriceNum - bPriceNum : bPriceNum - aPriceNum
        }

        if (sortBy === "priority") {
          const priorityValue = { high: 3, medium: 2, low: 1 }
          return sortOrder === "asc"
            ? priorityValue[a.priority || "low"] - priorityValue[b.priority || "low"]
            : priorityValue[b.priority || "low"] - priorityValue[a.priority || "low"]
        }

        return 0
      })
  }

  const filteredRequests = getFilteredRequests()

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800"
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "accepted":
      case "started":
      case "in-progress":
        return "bg-blue-100 text-blue-800"
      case "cancelled":
      case "rejected":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-200 text-red-800"
      case "medium":
        return "bg-orange-200 text-orange-800"
      case "low":
        return "bg-gray-200 text-gray-800"
      default:
        return "bg-gray-200 text-gray-800"
    }
  }

  const requestCounts = {
    all: filteredRequests.length,
    pending: filteredRequests.filter((r) => r.status === "pending").length,
    completed: filteredRequests.filter((r) => r.status === "completed").length,
    accepted: filteredRequests.filter((r) => r.status === "accepted" || r.status === "started").length,
    cancelled: filteredRequests.filter((r) => r.status === "cancelled" || r.status === "rejected").length,
    "in-progress": filteredRequests.filter((r) => r.status === "in-progress").length,
  }

  const toggleExpandRequest = (requestId) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null)
    } else {
      setExpandedRequestId(requestId)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <p className="text-xl font-semibold">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <p className="text-xl font-semibold text-red-600">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col items-start gap-4">
            {userId && (
              <button
                onClick={() => navigate(`/user-detail/${userId}`)}
                className="flex items-center text-white text-lg font-semibold transform transition-transform duration-300 hover:scale-105 hover:text-yellow-300"
              >
                <FaArrowLeft className="mr-2" /> Back to User Details
              </button>
            )}
            <h1 className="text-4xl font-bold text-white">
              {userType === "User" ? "User Ride Requests" : "Driver Post Requests"}
            </h1>
          </div>
        </div>

        {/* Requests List */}
        <div className="bg-white p-6 rounded-xl shadow-md">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {userType === "User" ? "Ride Requests by Users" : "Post Requests by Drivers"}
            </h2>

            <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
              >
                <FaFilter className="mr-2" /> Filters
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="date">Date</option>
                    <option value="price">Price</option>
                    <option value="priority">Priority</option>
                  </select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm("")
                      setSortBy("date")
                      setSortOrder("desc")
                      setActiveTab("all")
                    }}
                    className="p-2 text-sm text-teal-600 hover:text-teal-800 hover:underline"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Request Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 bg-gray-100 p-2 rounded-lg overflow-x-auto">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-4 py-2 rounded-md text-lg font-medium transition-colors ${
                activeTab === "all" ? "bg-white text-teal-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              All Requests{" "}
              <span className="ml-2 bg-gray-200 text-gray-700 px-2.5 py-1 rounded-full text-sm">
                {requestCounts.all}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("pending")}
              className={`px-4 py-2 rounded-md text-lg font-medium transition-colors ${
                activeTab === "pending" ? "bg-white text-yellow-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Pending{" "}
              <span className="ml-2 bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-full text-sm">
                {requestCounts.pending}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("accepted")}
              className={`px-4 py-2 rounded-md text-lg font-medium transition-colors ${
                activeTab === "accepted" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Accepted{" "}
              <span className="ml-2 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-sm">
                {requestCounts.accepted}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`px-4 py-2 rounded-md text-lg font-medium transition-colors ${
                activeTab === "completed" ? "bg-white text-green-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Completed{" "}
              <span className="ml-2 bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-sm">
                {requestCounts.completed}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("cancelled")}
              className={`px-4 py-2 rounded-md text-lg font-medium transition-colors ${
                activeTab === "cancelled" ? "bg-white text-red-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Cancelled{" "}
              <span className="ml-2 bg-red-100 text-red-700 px-2.5 py-1 rounded-full text-sm">
                {requestCounts.cancelled}
              </span>
            </button>
          </div>

          {/* Requests Cards */}
          <div className="space-y-6">
            {filteredRequests.length > 0 ? (
              filteredRequests.map((request) => {
                const isExpanded = expandedRequestId === request.id
                const isEditing = editingRequestId === request.id

                return (
                  <div
                    key={request.id}
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all"
                  >
                    {/* Request Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div className="flex items-center space-x-3 mb-2 md:mb-0">
                        <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-lg font-semibold">
                          #{request.id.slice(-8)}
                        </div>
                        <h4 className="text-xl font-bold text-gray-800">{request.type}</h4>
                        {request.priority && (
                          <span
                            className={`px-2 py-1 rounded text-sm font-medium ${getPriorityColor(request.priority)}`}
                          >
                            {request.priority} priority
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-3 py-1 rounded-full text-lg font-semibold ${getStatusColor(request.status)}`}
                        >
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Requester Information */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
                      <h5 className="font-semibold text-gray-700 mb-3 flex items-center">
                        <FaUser className="mr-2 text-teal-500" /> 
                        {userType === "User" ? "User Information" : "Driver Information"}
                      </h5>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mr-4 overflow-hidden">
                          {request.requesterImage ? (
                            <img 
                              src={request.requesterImage} 
                              alt={request.requesterName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <FaUser className="text-gray-500 text-xl" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{request.requesterName}</p>
                          <p className="text-sm text-gray-500">ID: {request.requesterId}</p>
                          {request.verificationCode && (
                            <p className="text-sm text-blue-600">Verification Code: {request.verificationCode}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Edit Form */}
                    {isEditing && (
                      <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
                        <h5 className="font-semibold text-gray-700 mb-4 flex items-center">
                          <FaEdit className="mr-2 text-teal-500" /> Edit Request Status
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                              value={editFormData.status || ""}
                              onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                              <option value="pending">Pending</option>
                              <option value="accepted">Accepted</option>
                              <option value="started">Started</option>
                              <option value="rejected">Rejected</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={() => handleSaveEdit(request.id)}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                          >
                            <FaSave className="mr-2" /> Save Changes
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Request Details Summary */}
                    {!isEditing && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                        <h5 className="font-semibold text-gray-700 mb-4">Request Summary</h5>
                        
                        {/* Package Image */}
                        {request.packageImage && (
                          <div className="mb-4">
                            <img 
                              src={request.packageImage} 
                              alt="Package"
                              className="w-32 h-32 object-cover rounded-lg mx-auto"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* <div className="space-y-2">
                            <div className="flex items-start">
                              <FaMapMarkerAlt className="text-teal-500 mr-2 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-gray-500">From:</p>
                                <p className="text-gray-700 font-medium">{request.from}</p>
                              </div>
                            </div>
                            <div className="flex items-start">
                              <FaMapMarkerAlt className="text-red-500 mr-2 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-gray-500">To:</p>
                                <p className="text-gray-700 font-medium">{request.to}</p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <FaCalendarAlt className="text-teal-500 mr-2" />
                              <span className="text-gray-700">
                                {request.date} at {request.time}
                              </span>
                            </div>
                          </div> */}
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <FaMoneyBillWave className="text-teal-500 mr-2" />
                              <span className="text-gray-700">Cost: {request.cost}</span>
                            </div>
                            <div className="flex items-center">
                              <FaBox className="text-teal-500 mr-2" />
                              <span className="text-gray-700">Size: {request.size} | Weight: {request.weight}</span>
                            </div>
                            <div className="flex items-center">
                              <FaRulerHorizontal className="text-teal-500 mr-2" />
                              <span className="text-gray-700">Distance: {request.distance}</span>
                            </div>
                            {userType === "Driver" && request.availableSpace && (
                              <div className="flex items-center">
                                <FaCar className="text-teal-500 mr-2" />
                                <span className="text-gray-700">
                                  Vehicle: {request.vehicleType} | Seats: {request.availableSpace}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {request.specialInstructions && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-gray-600">
                              <strong>Special Instructions:</strong> {request.specialInstructions}
                            </p>
                          </div>
                        )}

                        <div className="mt-4 text-center">
                          <button
                            onClick={() => toggleExpandRequest(request.id)}
                            className="text-teal-600 hover:text-teal-800 text-lg font-medium flex items-center justify-center mx-auto"
                          >
                            <FaInfoCircle className="mr-1" />
                            {isExpanded ? "Hide Details" : "Show Detailed Information"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expanded Details Section */}
                    {isExpanded && !isEditing && (
                      <div className="mt-4 space-y-6">
                        <div className="bg-white p-6 rounded-lg border border-gray-200">
                          <h5 className="font-semibold text-gray-700 mb-4 text-lg">
                            Complete Request Information
                          </h5>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                              <h6 className="font-medium text-gray-800">Request Details</h6>
                              <div className="space-y-2 text-sm">
                                <div><span className="font-medium">Full ID:</span> {request.id}</div>
                                <div><span className="font-medium">Type:</span> {request.type}</div>
                                <div><span className="font-medium">Status:</span> {request.status}</div>
                                <div><span className="font-medium">Created:</span> {new Date(request.createdAt).toLocaleString()}</div>
                                <div><span className="font-medium">Description:</span> {request.description}</div>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <h6 className="font-medium text-gray-800">Additional Information</h6>
                              <div className="space-y-2 text-sm">
                                <div><span className="font-medium">Requester:</span> {request.requesterName}</div>
                                <div><span className="font-medium">Requester ID:</span> {request.requesterId}</div>
                                {userType === "User" && (
                                  <div><span className="font-medium">Driver:</span> {request.driverName || 'N/A'}</div>
                                )}
                                {userType === "Driver" && (
                                  <div><span className="font-medium">Poster:</span> {request.posterName || 'N/A'}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {!isEditing && (
                      <div className="flex flex-wrap gap-3 mt-4">
                        <button
                          onClick={() => handleEditRequest(request)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                        >
                          <FaEdit className="mr-2" /> Edit Status
                        </button>
                        
                        <button
                          onClick={() => handleDeleteRequest(request.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                        >
                          <FaTimes className="mr-2" /> Delete Request
                        </button>

                        {(request.status === "pending" || request.status === "accepted") && (
                          <button
                            onClick={() => handleCancelRequest(request.id)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                          >
                            <FaTimes className="mr-2" /> Cancel Request
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-600 mb-2">No Requests Found</h4>
                <p className="text-gray-500">
                  {searchTerm
                    ? "No requests match your search criteria. Try adjusting your filters."
                    : activeTab !== "all"
                      ? `No ${activeTab} requests found.`
                      : `No requests found for ${userType.toLowerCase()}s.`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Cancel Request</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel this request? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmCancelRequest}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1"
              >
                Yes, Cancel Request
              </button>
              <button
                onClick={() => setShowCancelModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1"
              >
                Keep Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserRequestsPage